import React, { useState, useEffect } from 'react';
import { usePayment } from '../../context/PaymentContext';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';
import './PaymentGateway.css';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface PaymentGatewayProps {
  plan: any;
  onClose: () => void;
  onSuccess: () => void;
}

const PaymentGateway: React.FC<PaymentGatewayProps> = ({ plan, onClose, onSuccess }) => {
  const { createOrder, verifyPayment, loading, error, order } = usePayment();
  const { user } = useAuth();
  const [paymentStep, setPaymentStep] = useState<'summary' | 'processing' | 'success' | 'failed'>('summary');
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => {
      console.error('Failed to load Razorpay script');
      setPaymentStep('failed');
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (order && razorpayLoaded && window.Razorpay) {
      openRazorpayCheckout();
    }
  }, [order, razorpayLoaded]);

  const openRazorpayCheckout = () => {
    if (!window.Razorpay || !order || !user) return;

    setPaymentStep('processing');

    const options = {
      key: 'rzp_test_SHCQZMQFoBaboC',
      amount: order.amount,
      currency: order.currency,
      name: 'AstroML Bot',
      description: `${plan.name} - ${plan.duration}`,
      image: 'https://cdn-icons-png.flaticon.com/512/2583/2583130.png',
      order_id: order.id,
      handler: async function (response: any) {
        try {
          const verified = await verifyPayment(response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
          if (verified) {
            setPaymentStep('success');
            setTimeout(() => {
              onSuccess();
              onClose();
            }, 3000);
          } else {
            setPaymentStep('failed');
          }
        } catch (error) {
          console.error('Payment verification failed:', error);
          setPaymentStep('failed');
        }
      },
      prefill: {
        name: user.name,
        email: user.email,
        contact: user.phone || ''
      },
      notes: {
        plan_type: plan.type,
        plan_name: plan.name,
        user_id: user.id,
        user_email: user.email
      },
      theme: {
        color: 'var(--text-primary)'
      },
      modal: {
        ondismiss: function () {
          setPaymentStep('summary');
        },
        escape: false,
        handleback: false,
        confirm_close: true,
        animation: 'slide'
      },
      callback_url: `${window.location.origin}/payment/return`,
      redirect: true
    };

    const rzp = new window.Razorpay(options);
    
    rzp.on('payment.failed', function (response: any) {
      console.error('Payment failed:', response.error);
      setPaymentStep('failed');
    });

    rzp.open();
  };

  const handleProceedToPayment = async () => {
    if (!razorpayLoaded) {
      alert('Payment gateway is loading. Please wait...');
      return;
    }

    try {
      await createOrder(plan);
    } catch (error) {
      console.error('Failed to create order:', error);
      setPaymentStep('failed');
    }
  };

  const handleRetry = () => {
    setPaymentStep('summary');
  };

  if (loading && paymentStep === 'processing') {
    return (
      <div className="payment-gateway-overlay">
        <div className="payment-gateway-container">
          <div className="processing-state">
            <div className="spinner"></div>
            <h2>Processing Payment...</h2>
            <p>Please wait while we process your payment</p>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStep === 'success') {
    return (
      <div className="payment-gateway-overlay">
        <div className="payment-gateway-container">
          <div className="success-state">
            <div className="success-icon">✅</div>
            <h2>Payment Successful!</h2>
            <p>Your {plan.name} has been activated successfully.</p>
            <p>Redirecting to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStep === 'failed') {
    return (
      <div className="payment-gateway-overlay">
        <div className="payment-gateway-container">
          <div className="failed-state">
            <div className="failed-icon">❌</div>
            <h2>Payment Failed</h2>
            <p>{error || 'Something went wrong with your payment. Please try again.'}</p>
            <div className="failed-actions">
              <button onClick={handleRetry} className="retry-button">
                Try Again
              </button>
              <button onClick={onClose} className="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-gateway-overlay">
      <div className="payment-gateway-container">
        <div className="payment-header">
          <h2>🔐 Secure Payment</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="payment-summary">
          <div className="plan-details">
            <h3>{plan.name}</h3>
            <p className="price">₹{plan.price}</p>
            <p className="duration">{plan.duration}</p>
          </div>

          <div className="features-list">
            <h4>🎯 What You'll Get:</h4>
            <ul>
              {plan.features.map((feature: string, index: number) => (
                <li key={index}>✓ {feature}</li>
              ))}
            </ul>
          </div>

          <div className="user-details">
            <h4>👤 Account Details:</h4>
            <p><strong>Name:</strong> {user?.name}</p>
            <p><strong>Email:</strong> {user?.email}</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="payment-security">
            <div className="security-badges">
              <span className="security-badge">🔒 Secured by Razorpay</span>
              <span className="security-badge">💳 PCI DSS Compliant</span>
              <span className="security-badge">🛡️ 256-bit Encryption</span>
            </div>
          </div>
        </div>

        <div className="payment-actions">
          <button 
            onClick={handleProceedToPayment}
            className="pay-button"
            disabled={loading || !razorpayLoaded}
          >
            {loading ? 'Creating Order...' : `Pay ₹${plan.price}`}
          </button>
          
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentGateway;
